import { useTranslation } from 'react-i18next'

import {
  FLOATING_ELEMENT_PLACEMENT,
  NOTIFICATION_TYPE,
} from '../../../../../../constants/common'
import { useContentOverflow } from '../../../../../../hooks'
import Button, { BUTTON_HEIGHT_32 } from '../../../../../Button'
import { FileIcon } from '../../../../../FileIcon'
import { FloatingTooltip } from '../../../../../Floating'
import { ICON_HEIGHT_16 } from '../../../../../Icon'
import { NotificationInline } from '../../../../../Notification'
import { Status, STATUS_SIZE_20 } from '../../../../../Status'
import Text from '../../../../../Text'
import {
  APPLICANT_CREDIT_CHECK_PROVIDER_TEXT_COLOR,
  QA_NAME_APPLICANT_CREDIT_CHECK_SUGGESTION,
  QA_NAME_APPLICANT_CREDIT_CHECK_SUGGESTION_DISMISS,
  QA_NAME_APPLICANT_CREDIT_CHECK_SUGGESTION_REVIEW,
  QA_NAME_APPLICANT_CREDIT_CHECK_SUGGESTION_SOURCE,
} from '../ApplicantCreditCheckBlock.constants'
import {
  getApplicantCreditCheckSuggestionActionsState,
  getApplicantCreditCheckSuggestionFileProps,
  getApplicantCreditCheckSuggestionProviderText,
  getApplicantCreditCheckSuggestionText,
} from '../ApplicantCreditCheckBlock.utils'

import * as styles from './ApplicantCreditCheckSuggestion.module.css'

function ApplicantCreditCheckSuggestion({
  applicantData,
  isPreview,
  isSubmitting,
  suggestion,
  onClickDismiss,
  onClickReview,
}) {
  const { t } = useTranslation()

  const { overflow, setContentOverflowRef } = useContentOverflow()

  const { isDismissHidden, isReviewHidden, tooltipKey } =
    getApplicantCreditCheckSuggestionActionsState({
      applicantData,
      isPreview,
      suggestion,
    })

  const commonActionProps = {
    disabled: Boolean(tooltipKey) || isSubmitting,
    height: BUTTON_HEIGHT_32,
    tooltip: tooltipKey ? t(tooltipKey) : null,
    tooltipPlacement: FLOATING_ELEMENT_PLACEMENT.top,
  }

  function handleClickReview() {
    onClickReview(suggestion)
  }

  function handleClickDismiss() {
    onClickDismiss(suggestion)
  }

  return (
    <NotificationInline
      qaName={QA_NAME_APPLICANT_CREDIT_CHECK_SUGGESTION}
      text={getApplicantCreditCheckSuggestionText({ suggestion, t })}
      type={NOTIFICATION_TYPE.info}
    >
      <div className={styles.suggestionBody}>
        <div className={styles.suggestionHead}>
          <Status
            height={STATUS_SIZE_20}
            qaName={QA_NAME_APPLICANT_CREDIT_CHECK_SUGGESTION_SOURCE}
            text={t('RentalPortal.ApplicantCreditCheckSuggestionSourceKey')}
            textColor={APPLICANT_CREDIT_CHECK_PROVIDER_TEXT_COLOR}
          />
          <div className={styles.suggestionFile}>
            <FileIcon
              {...getApplicantCreditCheckSuggestionFileProps(suggestion.file)}
              size={ICON_HEIGHT_16}
            />
            <FloatingTooltip
              content={suggestion.file.name}
              disabled={!overflow}
              placement={FLOATING_ELEMENT_PLACEMENT.top}
            >
              <Text
                className={styles.documentLabel}
                ref={setContentOverflowRef}
              >
                {suggestion.file.name}
              </Text>
            </FloatingTooltip>
          </div>
        </div>

        <div className={styles.lines}>
          <Text isSecondary className={styles.line}>
            {getApplicantCreditCheckSuggestionProviderText({
              creditCheck: applicantData?.credit_check,
              suggestion,
              t,
            })}
          </Text>
          <Text isSecondary className={styles.line}>
            {t('RentalPortal.ApplicantCreditCheckSuggestionNothingRecordedKey')}
          </Text>
        </div>

        {isDismissHidden && isReviewHidden ? null : (
          <div className={styles.suggestionActions}>
            {isReviewHidden ? null : (
              <Button.Contained
                {...commonActionProps}
                isLoading={isSubmitting}
                qaName={QA_NAME_APPLICANT_CREDIT_CHECK_SUGGESTION_REVIEW}
                text={t(
                  'RentalPortal.ApplicantCreditCheckSuggestionReviewBtnKey'
                )}
                onClick={handleClickReview}
              />
            )}
            {isDismissHidden ? null : (
              <Button.Outlined
                {...commonActionProps}
                isGrey
                qaName={QA_NAME_APPLICANT_CREDIT_CHECK_SUGGESTION_DISMISS}
                text={t(
                  'RentalPortal.ApplicantCreditCheckSuggestionDismissBtnKey'
                )}
                onClick={handleClickDismiss}
              />
            )}
          </div>
        )}
      </div>
    </NotificationInline>
  )
}

export default ApplicantCreditCheckSuggestion
